<template>
  <v-dialog v-model="isVisible" width="50%" style="min-width: 350px" persistent>
    <v-card>
      <v-card-title>Add GeoJSON or WKT</v-card-title>

      <v-card-text>
        <v-form ref="form" @submit.prevent="submit" lazy-validation>
          <div class="d-flex flex-column">
            <div class="flex-shrink">
              <v-radio-group v-model="type" row @change="onTypeChange">
                <v-radio
                  id="geojson-radio"
                  label="GeoJSON"
                  value="geojson"
                ></v-radio>
                <v-radio
                  id="wkt-radio"
                  label="WKT (Well-Known Text)"
                  value="wkt"
                ></v-radio>
                <v-radio id="bbox-radio" label="BBox" value="bbox"></v-radio>
              </v-radio-group>
            </div>
            <div class="flex-grow">
              <v-textarea
                id="geojson-text"
                v-if="type === 'geojson'"
                ref="geojsonInput"
                v-model="geojson"
                @keydown.enter.prevent="submit"
                :rules="geojsonRules"
                :placeholder="geojsonPlaceholder"
                persistent-placeholder
                class="textAreaInput"
              >
                <template #label>
                  {{ type === "geojson" ? "GeoJSON" : "WKT" }}&nbsp;<span
                    class="primary--text"
                    >(Required)</span
                  >
                </template>
              </v-textarea>
              <v-textarea
                id="wkt-text"
                v-else-if="type === 'wkt'"
                ref="wktInput"
                v-model="wkt"
                @keydown.enter.prevent="submit"
                :rules="wktRules"
                :placeholder="wktPlaceholder"
                persistent-placeholder
                class="textAreaInput"
              >
                <template #label>
                  {{ type === "geojson" ? "GeoJSON" : "WKT" }}&nbsp;<span
                    class="primary--text"
                    >(Required)</span
                  >
                </template>
              </v-textarea>
              <v-textarea
                id="bbox-text"
                v-else
                ref="bboxInput"
                v-model="bbox"
                @keydown.enter.prevent="submit"
                :rules="bboxRules"
                :placeholder="bboxPlaceholder"
                persistent-placeholder
                class="textAreaInput"
              >
                <template #label>
                  {{ "BBox" }}&nbsp;<span class="primary--text"
                    >(Required)</span
                  >
                </template>
              </v-textarea>
            </div>
          </div>
        </v-form>
      </v-card-text>
      <v-card-actions class="pa-6 pt-0">
        <v-spacer></v-spacer>
        <v-btn outlined @click.prevent="cancel">Cancel</v-btn>
        <v-btn
          id="add-button"
          color="primary"
          class="ml-6 black--text"
          :disabled="!isAoiValid()"
          @click.prevent="submit"
        >
          Add
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import gjv from "geojson-validation";
import Wkt from "wicket";
import turfBboxPolygon from "@turf/bbox-polygon";

export default {
  data() {
    return {
      isVisible: false,
      type: "geojson",
      geojson: "",
      wkt: "",
      bbox: "",
      geojsonPlaceholder: `{
        "type": "FeatureCollection",
        "features": [
          {
            "type": "Feature",
            "properties": {},
            "geometry": {
              "type": "Polygon",
              "coordinates": [
                [
                  [
                    -105.00805377960205,
                    39.90861699713222
                  ],
                  [
                    -104.99475002288818,
                    39.90861699713222
                  ],
                  [
                    -104.99475002288818,
                    39.91673105400229
                  ],
                  [
                    -105.00805377960205,
                    39.91673105400229
                  ],
                  [
                    -105.00805377960205,
                    39.90861699713222
                  ]
                ]
              ]
            }
          }
        ]
      }`,
      wktPlaceholder: `POLYGON ((-105.00805377960205 39.90861699713222, -104.99475002288818 39.90861699713222, -104.99475002288818 39.91673105400229, -105.00805377960205 39.91673105400229, -105.00805377960205 39.90861699713222))`,
      bboxPlaceholder: `[-2.035217, 7.460518, -1.918488, 7.527234]`,
    };
  },
  computed: {
    geojsonRules() {
      if (this.type === "geojson") {
        return [
          (v) => !!v || "GeoJSON is required",

          (v) => {
            let json = null;
            let valid = false;

            if (v) {
              try {
                json = JSON.parse(v);
                valid = gjv.valid(json);
              } catch (err) {
                return `Invalid GeoJSON`;
              }
            }

            return valid || "Invalid GeoJSON";
          },
        ];
      } else {
        return [];
      }
    },
    wktRules() {
      if (this.type === "wkt") {
        return [
          (v) => !!v || "WKT is required",
          (v) => {
            let valid = false;

            if (v) {
              try {
                let wkt = new Wkt.Wkt();
                wkt.read(v);
                wkt.toJson();
                valid = true;
              } catch (err) {
                return `Invalid WKT`;
              }
            }

            return valid || "Invalid WKT";
          },
        ];
      } else {
        return [];
      }
    },
    bboxRules() {
      if (this.type === "bbox") {
        return [
          (v) => !!v || "BBox is required",
          (v) => {
            let valid = false;

            if (v) {
              try {
                const json = JSON.parse(v);
                turfBboxPolygon(json);
                valid = true;
              } catch (err) {
                return `Invalid BBox`;
              }
            }

            return valid || "Invalid BBox";
          },
        ];
      } else {
        return [];
      }
    },
  },
  methods: {
    isAoiValid() {
      try {
        if (this.type === "geojson" && this.geojson) {
          let json = null;
          json = JSON.parse(this.geojson);
          return gjv.valid(json);
        } else if (this.type === "wkt" && this.wkt) {
          let wkt = new Wkt.Wkt();
          wkt.read(this.wkt);
          wkt.toJson();
          return true;
        } else if (this.type === "bbox" && this.bbox) {
          const json = JSON.parse(this.bbox);
          turfBboxPolygon(json);
          return true;
        }
      } catch (err) {
        return false;
      }
    },
    onTypeChange() {
      this.focusOnTextArea(true);
      this.$refs.form.resetValidation();
    },
    format() {
      var ugly = this.text;
      var obj = JSON.parse(ugly);
      var pretty = JSON.stringify(obj, undefined, 4);
      this.text = pretty;
    },
    focusOnTextArea(select = false) {
      setTimeout(() => {
        let textArea = null;
        if (this.type === "geojson") {
          textArea = this.$refs.geojsonInput;
        } else if (this.type === "wkt") {
          textArea = this.$refs.wktInput;
        } else {
          textArea = this.$refs.bboxInput;
        }

        if (textArea) {
          textArea.focus();
          if (select) {
            textArea.$el.querySelector("textarea").select();
          }
        }
      }, 0);
    },
    show() {
      this.isVisible = true;
      this.focusOnTextArea(true);
    },
    cancel() {
      this.isVisible = false;
      this.reset();
    },
    submit() {
      if (this.$refs.form.validate()) {
        this.isVisible = false;
        let featureCollection = null;
        if (this.type === "geojson") {
          featureCollection = this.convertToFeatureCollection(
            JSON.parse(this.geojson)
          );
        } else if (this.type === "wkt") {
          featureCollection = this.convertWktToGeoJson(this.wkt);
        } else {
          featureCollection = this.convertBboxToGeoJson(JSON.parse(this.bbox));
        }
        this.$emit("submit", featureCollection);
        this.reset();
      }
    },
    reset() {
      this.geojson = "";
      this.wkt = "";
      this.bbox = "";
      this.$refs.form.resetValidation();
    },
    convertToFeatureCollection(geojson) {
      // if it's not a feature collection
      if (
        geojson.type !== "FeatureCollection" ||
        !geojson.features ||
        !Array.isArray(geojson.features)
      ) {
        // geojson type is a feature, so make it a feature collection
        if (geojson.type === "Feature") {
          return {
            type: "FeatureCollection",
            features: [geojson],
          };
        } else {
          // geojson type is probably a Polygon, Multipolygon, or another geometry type
          return {
            type: "FeatureCollection",
            features: [
              {
                type: "Feature",
                properties: {},
                geometry: geojson,
              },
            ],
          };
        }
      }

      return geojson;
    },
    convertWktToGeoJson(wktText) {
      try {
        let wkt = new Wkt.Wkt();
        wkt.read(wktText);
        const geometry = wkt.toJson();

        return {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              properties: {},
              geometry,
            },
          ],
        };
      } catch (err) {
        this.$log.error(err);
      }

      return null;
    },
    convertBboxToGeoJson(bbox) {
      try {
        const bboxPolygon = turfBboxPolygon(bbox);
        const geometry = bboxPolygon.geometry;

        return {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              properties: {},
              geometry,
            },
          ],
        };
      } catch (err) {
        this.$log.error(err);
      }

      return null;
    },
  },
};
</script>

<style lang="scss">
.textAreaInput.v-textarea textarea {
  line-height: 1.2rem;
  min-height: 250px;
  height: 50vh;
}

.v-application .error--text {
  color: #df323b !important;
}
</style>
